import { useCallback, useRef } from 'react'

import { ApiError } from '@/shared/api/client'

/** The API's code for a key sent again with a different request (GW-28). */
const reusedKeyCode = 'idempotency_key.reused'

/** The API's code for a request already done under the key whose answer it no longer shows (GW-28). */
const answerWithheldCode = 'idempotency_key.answer_withheld'

/**
 * Refusals that can come before the API reads the key's stored answer (sign-in, timeout, rate limit), or a
 * write never sent: they say nothing about an earlier attempt under the same key. A 403 never means it ran.
 */
const beforeTheKeyIsRead = new Set([401, 408, 429])

/**
 * Whether a keyed write's attempt is over: it succeeded (nothing thrown), or a 4xx says it ran nothing,
 * so no earlier attempt under its key ran either (the API answers a key that ran with its stored answer).
 * A page that handles `isAlreadyDone` checks it first.
 */
export function endsAttempt(error?: unknown): boolean {
  return (
    error === undefined ||
    (error instanceof ApiError &&
      error.status >= 400 &&
      error.status < 500 &&
      error.code !== reusedKeyCode &&
      !beforeTheKeyIsRead.has(error.status))
  )
}

/** Whether the API says the request was already done under its key, though it no longer shows the answer. */
export function isAlreadyDone(error: unknown): boolean {
  return error instanceof ApiError && error.code === answerWithheldCode
}

/**
 * The `Idempotency-Key` for a create page's POST. Only a 4xx answer says for sure the record was
 * not made; after any other failure (no answer, a 5xx, or an answer cut off) the attempt keeps its
 * key, so the next Save retries the same create and the API answers with the record it made rather
 * than making another. A success or a 4xx ends the attempt; the next create gets a new key. The
 * exceptions keep the key, so no second record can be made under a new one: 409
 * `idempotency_key.reused` (the form changed since the unclear attempt), and 401, 408 and 429, which
 * can be answered before the key is read.
 */
export function useCreateKey(): {
  /** The key for this attempt. */
  readonly key: () => string
  /** Records how the attempt ended: pass what the POST threw, or nothing when it succeeded. */
  readonly settle: (error?: unknown) => void
  /** Whether an attempt with an unclear outcome still holds its key, so the next Save retries it. */
  readonly isHeld: () => boolean
} {
  const current = useRef<string | null>(null)
  const key = useCallback(() => {
    current.current ??= crypto.randomUUID()
    return current.current
  }, [])
  const settle = useCallback((error?: unknown) => {
    if (endsAttempt(error)) {
      current.current = null
    }
  }, [])
  const isHeld = useCallback(() => current.current !== null, [])
  return { key, settle, isHeld }
}
